/**
 * Formats a number as currency with thousands separators and 2 decimal places.
 *
 * `maxFractionDigits` raises the ceiling above the default 2 so sub-cent values
 * (e.g. a per-round model cost of $0.0042) don't collapse to `$0.00`. The floor
 * stays at 2 digits, so whole and cent amounts render unchanged; the extra
 * digits appear only when the value actually has them.
 *
 * `currencyCode` is for the few figures that carry the issuing bank's own
 * currency rather than the household's — a credit card statement, say. Omitting
 * it keeps the plain `$` prefix, so every existing call site is unchanged.
 *
 * `symbol: false` drops the currency symbol entirely, for the callers that draw
 * one themselves — an entity chip renders a currency glyph beside its label, and
 * `$` in both places reads "$ $75.15". The sign is kept; a currency code is not.
 *
 * @example toCurrency(1234.56)                          // "$1,234.56"
 * @example toCurrency(-1234.56)                         // "-$1,234.56"
 * @example toCurrency(1234.56, { showSign: true })      // "+$1,234.56"
 * @example toCurrency(-1234.56, { showSign: false })    // "-$1,234.56"
 * @example toCurrency(0.0042, { maxFractionDigits: 4 }) // "$0.0042"
 * @example toCurrency(5, { maxFractionDigits: 4 })      // "$5.00"
 * @example toCurrency(1234.56, { currencyCode: 'CAD' }) // "CA$1,234.56"
 * @example toCurrency(1234.56, { currencyCode: 'EUR' }) // "€1,234.56"
 * @example toCurrency(1234.56, { symbol: false })       // "1,234.56"
 */
export default function toCurrency(
  value: number | string,
  args?: { showSign?: boolean; maxFractionDigits?: number; currencyCode?: string | null; symbol?: boolean }
): string {
  const { showSign = false, maxFractionDigits = 2, currencyCode, symbol = true } = args ?? {};
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const absValue = Math.abs(num);
  const sign = showSign ? (num < 0 ? '-' : num > 0 ? '+' : '') : num < 0 ? '-' : '';
  const fractionDigits = {
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.max(2, maxFractionDigits),
  };

  if (!symbol) {
    return `${sign}${absValue.toLocaleString(undefined, fractionDigits)}`;
  }

  if (currencyCode && currencyCode !== 'USD') {
    // An unrecognized code throws inside `Intl`, and a bank sending one is not
    // worth a blank screen — fall back to the bare number with the code beside it.
    try {
      return `${sign}${absValue.toLocaleString(undefined, { style: 'currency', currency: currencyCode, ...fractionDigits })}`;
    } catch {
      return `${sign}${absValue.toLocaleString(undefined, fractionDigits)} ${currencyCode}`;
    }
  }

  return `${sign}$${absValue.toLocaleString(undefined, fractionDigits)}`;
}
